import Lenis from '@studio-freight/lenis';
import { SoundEffect, playSound } from './soundEffects';

let lenis: Lenis | null = null;
let rafId: number | null = null;

/**
 * Initialize Lenis smooth scrolling
 */
export const initLenis = (): Lenis => {
    if (lenis) {
        return lenis;
    }

    lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smooth: true,
        mouseMultiplier: 1,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false,
    });

    // Add scroll sound effects with throttling
    let lastSoundTime = 0;
    const soundThrottle = 100; // ms

    lenis.on('scroll', ({ velocity, direction }) => {
        const now = Date.now();
        if (Math.abs(velocity) > 0.5 && now - lastSoundTime > soundThrottle) {
            const normalizedVelocity = Math.min(Math.abs(velocity) / 5, 1);
            const volume = 0.05 + normalizedVelocity * 0.05;
            playSound(SoundEffect.SCROLL, volume);
            lastSoundTime = now;
        }
    });

    return lenis;
};

/**
 * Start the Lenis animation loop
 */
export const startLenis = (): void => {
    if (!lenis) {
        console.warn('Lenis not initialized. Call initLenis() first.');
        return;
    }

    // Cancel any existing RAF loop
    if (rafId) {
        cancelAnimationFrame(rafId);
    }

    function raf(time: number) {
        lenis?.raf(time);
        rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
};

/**
 * Get header height with better detection
 */
const getHeaderHeight = (): number => {
    const header = document.querySelector('header, .header, nav, .navbar');
    if (!header) return 0;
    
    const rect = header.getBoundingClientRect();
    const styles = window.getComputedStyle(header);
    const isFixed = styles.position === 'fixed' || styles.position === 'sticky';
    
    return isFixed ? rect.height : 0;
};

/**
 * Resolve target element
 */
const resolveTarget = (target: string | HTMLElement | number): HTMLElement | number => {
    if (typeof target === 'number') {
        return target;
    }
    
    if (typeof target === 'string') {
        // Handle anchor links (remove # if present)
        const cleanTarget = target.startsWith('#') ? target.slice(1) : target;
        
        // Try to find element by ID first
        let element = document.getElementById(cleanTarget);
        
        // If not found by ID, try querySelector
        if (!element) {
            element = document.querySelector(target);
        }
        
        // If still not found and it looks like an anchor, try with #
        if (!element && !target.startsWith('#')) {
            element = document.querySelector(`#${cleanTarget}`);
        }
        
        if (!element) {
            console.warn(`Target element not found: ${target}`);
            return 0;
        }
        
        return element;
    }
    
    return target;
};

/**
 * Smooth scroll to a target element or anchor
 */
export const scrollTo = (
    target: string | HTMLElement | number,
    options: {
        offset?: number;
        duration?: number;
        easing?: (t: number) => number;
        immediate?: boolean;
        lock?: boolean;
        force?: boolean;
        onComplete?: () => void;
        enableSound?: boolean;
    } = {}
): void => {
    if (!lenis) {
        console.warn('Lenis not initialized. Call initLenis() first.');
        return;
    }

    const {
        offset = 0,
        duration,
        easing,
        immediate = false,
        lock = false,
        force = false,
        onComplete,
        enableSound = true
    } = options;

    // Play scroll sound effect
    if (enableSound) {
        playSound(SoundEffect.SCROLL, 0.1);
    }

    // Resolve the target
    const resolvedTarget = resolveTarget(target);
    
    // Calculate header offset
    const headerHeight = getHeaderHeight();
    const additionalPadding = 40; // Extra space for better UX
    const finalOffset = offset - headerHeight - additionalPadding;

    // Use the resolved target
    lenis.scrollTo(resolvedTarget, {
        offset: finalOffset,
        duration,
        easing,
        immediate,
        lock,
        force,
        onComplete: () => {
            // Update URL hash if target is a string starting with #
            if (typeof target === 'string' && target.startsWith('#')) {
                history.pushState(null, '', target);
            }
            onComplete?.();
        }
    });
};

/**
 * Scroll to element by ID (convenience method)
 */
export const scrollToId = (id: string, options = {}): void => {
    const cleanId = id.startsWith('#') ? id : `#${id}`;
    scrollTo(cleanId, options);
};

/**
 * Get the current Lenis instance
 */
export const getLenis = (): Lenis | null => {
    return lenis;
};

/**
 * Stop Lenis
 */
export const stopLenis = (): void => {
    if (lenis) {
        lenis.stop();
    }
};

/**
 * Start Lenis
 */
export const resumeLenis = (): void => {
    if (lenis) {
        lenis.start();
    }
};

/**
 * Destroy Lenis instance
 */
export const destroyLenis = (): void => {
    if (lenis) {
        lenis.destroy();
        lenis = null;
    }
    
    if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
    }
};

/**
 * Resize Lenis (useful for window resize events)
 */
export const resizeLenis = (): void => {
    if (lenis) {
        lenis.resize();
    }
};